// components/ProductSelector.js
import React, { useState, useEffect, useRef } from 'react';
import { Search, Clock, Grid, ChevronDown } from 'lucide-react';
import { 
  searchProducts, 
  getProductsByCategory, 
  productCategories,
  getRecentProducts,
  addToRecentProducts 
} from '../data/products';

const ProductSelector = ({ onSelectProduct, selectedProduct, placeholder = "품목 선택 또는 검색..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'recent', 'category'
  
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setRecentProducts(getRecentProducts());
  }, []);

  useEffect(() => {
    if (activeTab === 'search') {
      setFilteredProducts(searchProducts(searchQuery));
    } else if (activeTab === 'category') {
      setFilteredProducts(getProductsByCategory(selectedCategory));
    }
  }, [searchQuery, selectedCategory, activeTab]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProductSelect = (product) => {
    onSelectProduct(product);
    addToRecentProducts(product);
    setRecentProducts(getRecentProducts());
    setIsOpen(false);
    setSearchQuery(''); // 검색어 초기화
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // 선택된 품목이 없을 때만 최근 사용 탭 표시
    if (!selectedProduct) {
      setActiveTab('recent');
    } else {
      setActiveTab('search');
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // 선택된 품목이 있고, 사용자가 다른 내용을 입력하기 시작하면 선택 해제
    if (selectedProduct && query !== selectedProduct.name && query.trim() !== '') {
      onSelectProduct(null);
    }
    
    if (query.trim()) {
      setActiveTab('search');
    } else if (!selectedProduct) {
      setActiveTab('recent');
    }
  };

  const ProductItem = ({ product, onClick }) => (
    <div
      onClick={() => {
        onClick(product);
      }}
      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="font-medium text-gray-900">{product.name}</div>
          <div className="text-sm text-gray-500">{product.description}</div>
          <div className="text-xs text-gray-400 mt-1">
            {product.category} • {product.unitPrice.toLocaleString()}원/{product.unit}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 입력 필드 */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={selectedProduct ? selectedProduct.name : searchQuery}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <Search className="absolute right-2 top-2.5 w-4 h-4 text-gray-400" />
      </div>

      {/* 드롭다운 */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-hidden">
          {/* 탭 헤더 */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('recent')}
              className={`flex-1 px-4 py-2 text-sm font-medium ${
                activeTab === 'recent' 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-1" />
              최근 사용
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`flex-1 px-4 py-2 text-sm font-medium ${
                activeTab === 'search' 
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
            
              <Grid className="w-4 h-4 inline mr-1" />
              카테고리
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {/* 최근 사용 탭 */}
            {activeTab === 'recent' && (
              <div>
                {recentProducts.length > 0 ? (
                  recentProducts.map(product => (
                    <ProductItem
                      key={`recent-${product.id}`}
                      product={product}
                      onClick={handleProductSelect}
                    />
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    최근 사용한 품목이 없습니다
                  </div>
                )}
              </div>
            )}

            {/* 검색 탭 */}
            {activeTab === 'search' && (
              <div>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(product => (
                    <ProductItem
                      key={`search-${product.id}`}
                      product={product}
                      onClick={handleProductSelect}
                    />
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    {searchQuery ? '검색 결과가 없습니다' : '검색어를 입력해주세요'}
                  </div>
                )}
              </div>
            )}

            {/* 카테고리 탭 */}
            {activeTab === 'category' && (
              <div>
                {/* 카테고리 선택 */}
                <div className="p-3 border-b border-gray-200">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm"
                  >
                    <option value="전체">전체 카테고리</option>
                    {productCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                {/* 카테고리별 상품 목록 */}
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(product => (
                    <ProductItem
                      key={`category-${product.id}`}
                      product={product}
                      onClick={handleProductSelect}
                    />
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Grid className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    해당 카테고리에 상품이 없습니다
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;